import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { SUPPORTED_LANGS, type SupportedLang } from '../i18n';

const LANG_LABELS: Record<SupportedLang, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
};

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = (lang || 'en') as SupportedLang;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const switchLanguage = (newLang: SupportedLang) => {
    setOpen(false);
    if (newLang === currentLang) return;

    let pathWithoutLang = location.pathname;
    if (lang) {
      pathWithoutLang = pathWithoutLang.replace(`/${lang}`, '') || '/';
    }

    const newPath = newLang === 'en'
      ? pathWithoutLang
      : `/${newLang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;

    i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);
    navigate(newPath + location.search + location.hash);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors rounded-full hover:bg-white/5"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {/* Globe icon */}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A8.966 8.966 0 0 1 3 12c0-1.264.26-2.466.732-3.558" />
        </svg>
        <span>{currentLang.toUpperCase()}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 md:left-auto md:right-0 mt-2 w-40 rounded-xl bg-gray-900/95 backdrop-blur-xl border border-white/10 shadow-2xl py-1 z-50" role="listbox">
          {SUPPORTED_LANGS.map((l) => (
            <button
              key={l}
              role="option"
              aria-selected={currentLang === l}
              onClick={() => switchLanguage(l)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                currentLang === l
                  ? 'text-waffle-orange bg-waffle-orange/10'
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <span>{LANG_LABELS[l]}</span>
              {currentLang === l && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
